/**
 * Data table utilities
 */

import type { Column } from '@tanstack/react-table';
import type { CSSProperties } from 'react';

/**
 * Get common pinning styles for a column.
 *
 * Uses TanStack's `getStart()`/`getAfter()` for sticky offsets and
 * `getIsLastColumn()`/`getIsFirstColumn()` for boundary shadow detection.
 *
 * Returns empty object if column is not pinned.
 */
export function getCommonPinningStyles<TData>({
  column,
}: {
  column: Column<TData>;
}): CSSProperties {
  const isPinned = column.getIsPinned();
  if (!isPinned) return {};

  const isLastLeft = isPinned === 'left' && column.getIsLastColumn('left');
  const isFirstRight = isPinned === 'right' && column.getIsFirstColumn('right');

  return {
    position: 'sticky',
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    zIndex: 5,
    boxShadow: isLastLeft
      ? '2px 0 4px -1px rgba(0, 0, 0, 0.1)'
      : isFirstRight
        ? '-2px 0 4px -1px rgba(0, 0, 0, 0.1)'
        : undefined,
  };
}
